import { useState, useEffect } from 'react';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { HeroSection } from '../components/HeroSection';
import { TickerBar } from '../components/TickerBar';
import { MetaTags } from '../components/MetaTags';
import { Loader2, AlertCircle, ShoppingBag, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOPIFY_STORE_URL = 'https://darkar.co';

const ShopPage = () => {
  const [products, setProducts] = useState([]);
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannersLoading, setBannersLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProducts();
    fetchBanners();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      
      // Fetch products and custom order in parallel
      const [productsRes, orderRes] = await Promise.all([
        axios.get(`${API}/shopify/products`),
        axios.get(`${API}/shop-product-order`).catch(() => ({ data: [] }))
      ]);
      
      const shopifyProducts = productsRes.data.products;
      const customOrder = orderRes.data;
      
      // Create order map
      const orderMap = {};
      customOrder.forEach(item => {
        orderMap[item.handle] = item.order;
      });
      
      // Sort products by custom order, then by availability
      const sortedProducts = [...shopifyProducts].sort((a, b) => {
        const orderA = orderMap[a.handle] ?? 999;
        const orderB = orderMap[b.handle] ?? 999;
        
        if (orderA !== orderB) return orderA - orderB;
        
        // If same order, sort by availability (sold out last)
        const aAllSoldOut = a.variants?.every(v => v.available === false) ?? false;
        const bAllSoldOut = b.variants?.every(v => v.available === false) ?? false;
        
        if (aAllSoldOut && !bAllSoldOut) return 1;
        if (!aAllSoldOut && bAllSoldOut) return -1;
        return 0;
      });
      
      setProducts(sortedProducts);
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('No se pudieron cargar los productos. Intenta de nuevo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      setBannersLoading(true);
      const res = await axios.get(`${API}/shop-banners`);
      setBanners(res.data);
    } catch (err) {
      console.error('Error fetching shop banners:', err);
      setBanners([]);
    } finally {
      setBannersLoading(false);
    }
  };

  // Check if product is sold out (all variants unavailable)
  const isProductSoldOut = (product) => {
    return product.variants?.every(v => v.available === false) ?? false;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags 
        title="Tienda"
        description="Tienda oficial de Darkar Company - Playeras, accesorios y más"
        url="/tienda"
      />
      <Navbar />
      
      {/* Hero Carousel */}
      <HeroSection banners={banners} loading={bannersLoading} />
      
      {/* Ticker Bar */}
      <TickerBar />
      
      <main className="flex-1 pb-16">
        {/* Products Grid */}
        <div className="max-w-7xl mx-auto px-4 pt-10">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Loader2 className="animate-spin text-[#E91E8C] mb-4" size={48} />
              <p className="text-white/60">Cargando productos...</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <AlertCircle className="text-red-400 mb-4" size={48} />
              <p className="text-white/60">{error}</p>
              <button
                onClick={fetchProducts}
                className="mt-4 px-6 py-2 bg-[#E91E8C] text-white rounded-lg hover:bg-[#E91E8C]/80 transition-colors"
              >
                Reintentar
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <ShoppingBag className="text-white/20 mb-4" size={64} />
              <p className="text-white/60 text-lg">No hay productos disponibles</p>
              <p className="text-white/40 text-sm mt-2">Vuelve pronto, estamos preparando cosas increíbles.</p>
            </div>
          ) : (
            <div 
              className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6"
              data-testid="products-grid"
            >
              {products.map((product) => {
                const soldOut = isProductSoldOut(product);
                const productUrl = `${SHOPIFY_STORE_URL}/products/${product.handle}`;
                const price = product.variants?.[0]?.price;
                
                return (
                  <a
                    key={product.id}
                    href={productUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`group block bg-black hand-drawn-subtle overflow-hidden transition-transform hover:scale-[1.02] ${
                      soldOut ? 'grayscale opacity-70' : ''
                    }`}
                    style={{ borderWidth: '3px', borderColor: '#fff', borderStyle: 'solid' }}
                    data-testid={`product-card-${product.handle}`}
                  >
                    {/* Product Image */}
                    <div className="relative aspect-square overflow-hidden bg-white">
                      {(product.images?.[0]?.src || product.images?.[0]?.url) ? (
                        <img
                          src={product.images[0].src || product.images[0].url}
                          alt={product.title}
                          className="w-full h-full object-cover transition-transform group-hover:scale-105"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                          <ShoppingBag size={48} className="text-white/20" />
                        </div>
                      )}
                      
                      {/* Sold Out Badge */}
                      {soldOut && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded">
                          AGOTADO
                        </div>
                      )}
                      
                      {/* External Link Icon on Hover */}
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-[#E91E8C] p-3 rounded-full">
                          <ExternalLink size={24} className="text-white" />
                        </div>
                      </div>
                    </div>
                    
                    {/* Product Info */}
                    <div className="p-3 md:p-4">
                      <h3 className="text-white font-bold text-sm md:text-base line-clamp-2 group-hover:text-[#E91E8C] transition-colors">
                        {product.title}
                      </h3>
                      
                      {product.description && (
                        <p className="text-white/50 text-xs mt-1 line-clamp-2">
                          {product.description}
                        </p>
                      )}
                      
                      {price && (
                        <p className="text-[#E91E8C] font-bold text-base md:text-lg mt-2">
                          ${parseFloat(price).toFixed(2)}
                        </p>
                      )}
                      
                      <p className="text-white/40 text-xs mt-2 flex items-center gap-1">
                        <ExternalLink size={12} />
                        Ver en darkar.co
                      </p>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
